import * as React from 'react'
import { Tooltip } from 'react-tooltip'
import _ from 'underscore'
import { XhprofCallgraph, XhprofCallgraphNode } from '../../xhprof/XhprofCallgraph'
import D3Chart from './chart.d3'

interface Props {
    tree: XhprofCallgraph
    totalWalltime: number
    selectedNode: string
    rootNode: string
    dimensions: { width: number; height: number }
    onNodeSelected: CallgraphNodeEvent
    unfoldCalls: (calls: string[]) => void
    backToMain?: () => void
    onChartCreated?: (chart) => void
    defaultScaleOnSelect?: number
    navigationConfig: { home: boolean }
    expandLink?: React.ReactElement<any>
    showAsDeltas?: boolean
    centerOffset?: number
    marginLeft?: number
    memory?: boolean
    maxStartCount: number
    onChangeMaxStartCount: (maxCount: number) => void
}

type CallgraphNodeEvent = (node: XhprofCallgraphNode) => void

// origin of this number unknown, probably change this to something simpler in the future
const scaleModifier = 0.6070974421975235

export class Chart extends React.Component<Props> {
    public static defaultProps = {
        navigationConfig: { home: true },
        showAsDeltas: false,
        centerOffset: 0,
        marginLeft: 0,
        memory: false,
    }
    private svgElement = React.createRef<SVGSVGElement>()
    private chart

    public componentDidMount() {
        if (!this.chart) {
            this.chart = new D3Chart()

            if (this.props.onChartCreated) {
                this.props.onChartCreated(this.chart)
            }
        }

        this.chart.create(
            this.svgElement.current,
            this.props.dimensions.width - (this.props.marginLeft || 0),
            this.props.dimensions.height,
            this.props.onNodeSelected,
            this.props.unfoldCalls,
            this.props.showAsDeltas,
        )

        this.updateChart()
    }

    // @todo necessary or update every time + componentShouldUpdate?
    public componentDidUpdate(prevProps: Props, prevState) {
        const treeChanged = !_.isEqual(prevProps.tree, this.props.tree)
        const selectedNodeChanged = prevProps.selectedNode !== this.props.selectedNode
        const centerOffsetChanged = prevProps.centerOffset !== this.props.centerOffset
        const displayModeChanged = prevProps.memory !== this.props.memory
        const maxStartCountChanged = prevProps.maxStartCount !== this.props.maxStartCount
        // @todo check if this should be rate limited (delay for x milliseconds, only changes >y pixels)
        //       or if event is already rate limited
        const screenSizeChanged =
            prevProps.dimensions.width !== this.props.dimensions.width ||
            prevProps.dimensions.height !== this.props.dimensions.height ||
            prevProps.marginLeft !== this.props.marginLeft

        if (treeChanged || screenSizeChanged) {
            this.updateChart()
        }

        if (centerOffsetChanged || selectedNodeChanged || displayModeChanged || screenSizeChanged) {
            this.chart.selectNode(this.props.selectedNode)
        }

        if (maxStartCountChanged) {
            this.chart.zoomFull()
        }
    }

    public updateChart = () => {
        if (!this.props.tree) {
            return
        }

        const { tree, totalWalltime, selectedNode, centerOffset, memory } = this.props

        const sizeUpdate = {
            width: this.props.dimensions.width - (this.props.marginLeft || 0),
            height: this.props.dimensions.height,
        }

        this.chart.update(tree, totalWalltime, selectedNode, centerOffset, memory, sizeUpdate)
    }

    public render() {
        return (
            <React.Fragment>
                <svg ref={this.svgElement} style={{ marginLeft: this.props.marginLeft }} />
                <Tooltip id="callgraphTooltip" place="bottom-end" className="tw-react-tooltip" />
                <div className="btn-group controls">
                    {this.props.navigationConfig.home && (
                        <a
                            className="btn btn-default"
                            onClick={() => this.chart.zoomHome()}
                            data-tooltip-id={'callgraphTooltip'}
                            data-tooltip-content="Go to main() node"
                        >
                            <i className="fa fa-home" />
                        </a>
                    )}
                    <a
                        className="btn btn-default"
                        onClick={() => this.chart.zoomFull()}
                        data-tooltip-id="callgraphTooltip"
                        data-tooltip-content="show complete graph"
                    >
                        <i className="fa fa-arrows-alt" />
                    </a>
                    <a
                        className="btn btn-default"
                        onClick={() => this.chart.adaptScale(1 / scaleModifier)}
                        data-tooltip-id="callgraphTooltip"
                        data-tooltip-content="zoom in (shift+mouse-wheel)"
                    >
                        <i className="fa fa-search-plus" />
                    </a>
                    <a
                        className="btn btn-default"
                        onClick={() => this.chart.adaptScale(scaleModifier)}
                        data-tooltip-id="callgraphTooltip"
                        data-tooltip-content="zoom out (shift+mouse-wheel)"
                    >
                        <i className="fa fa-search-minus" />
                    </a>
                    <select
                        className="btn btn-default"
                        onChange={ev => {
                            this.props.onChangeMaxStartCount(parseInt(ev.target.value, 10))
                        }}
                        value={this.props.maxStartCount}
                        data-tooltip-id="callgraphTooltip"
                        data-tooltip-content="Number of heavy nodes to base the graph on."
                    >
                        {[3, 4, 5, 8, 12].map(nodes => (
                            <option value={nodes} key={nodes}>
                                {nodes}
                            </option>
                        ))}
                    </select>
                    {this.props.rootNode !== 'main()' && this.props.backToMain ? (
                        <a
                            className="btn btn-default"
                            onClick={this.props.backToMain}
                            data-tooltip-id="callgraphTooltip"
                            data-tooltip-content="back to main()"
                        >
                            <i className="fa fa-level-up" />
                        </a>
                    ) : null}
                    {this.props.expandLink ? this.props.expandLink : null}
                </div>
                <div className="clearfix" />
            </React.Fragment>
        )
    }
}
